import { Button, pxToRem } from '@modernhealth/vital-components';
import styled from '@emotion/styled';
import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
    MemberFilterPreferenceNode,
    MemberFilterPreferenceOptionNode,
} from 'src/generated/webapp_gql';
import chevronIcon from 'src/public/img/icon-chevron.svg';
import closeIcon from 'src/public/img/new-close-icon.svg';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { Modal } from 'src/shared/components/Modal';

import { color, font } from 'src/shared/styles/variables';
import { voidFunction } from 'src/shared/helpers/voidFunction';
import { useGetMemberFilterPreferencesQuery } from '../documents/getMemberFilterPreferences.generated';

const CategoryItemHolder = styled('div')({
    marginBottom: pxToRem(14),
});

const CategoryTitle = styled('h3')(
    {
        color: color.ebonyClay,
        marginBottom: '0.5rem',
    },
    font.heavy(font.size.large),
);

const CategoryTopics = styled('ul')({
    padding: 0,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
});

interface StepWrapperProps {
    active: boolean;
}

const CategoryTopic = styled.li<StepWrapperProps>(({ active }) => ({
    color: active ? color.white : color.blueBayoux,
    lineHeight: 1.3,
    marginTop: '0.5rem',
    marginRight: '0.5rem',
    border: `2px solid ${active ? color.cornflowerBlueDark : color.catskillWhite}`,
    padding: 10,
    borderRadius: 50,
    cursor: 'pointer',
    backgroundColor: active ? color.cornflowerBlueDark : color.white,
    ...font.heavy(font.size.base),
}));

interface CategoryItemProps extends MemberFilterPreferenceNode {
    key: string;
    handleSelect: (options: MemberFilterPreferenceOptionNode) => void;
    choices: string[];
}

const CategoryItem: React.FC<CategoryItemProps> = ({ name, options, choices, handleSelect }) => (
    <CategoryItemHolder>
        <CategoryTitle>{name}</CategoryTitle>
        <CategoryTopics>
            {options?.map((option: MemberFilterPreferenceOptionNode) => (
                <CategoryTopic
                    active={choices.includes(option?.name || '')}
                    key={option?.name + 'category-topic'}
                    onClick={() => handleSelect(option)}
                >
                    {option?.value}
                </CategoryTopic>
            ))}
        </CategoryTopics>
    </CategoryItemHolder>
);

const StyledModal = styled(Modal)({
    '& .ant-modal-content': {
        maxWidth: 500,
        minHeight: 300,
        margin: '0 auto',
        borderRadius: pxToRem(20),

        '& .ant-modal-body': {
            padding: pxToRem(28, 24, 30),
        },

        '& .ant-modal-header': {
            padding: pxToRem(24, 24, 18, 24),
            borderRadius: pxToRem(20, 20, 0, 0),
        },

        '& .ant-modal-close': {
            right: 24,
            top: 20,
        },
        '& .exitIcon': {
            margin: 0,
        },
    },
});

const ModalHeader = styled('h3')(
    {
        color: color.ebonyClay,
        textAlign: 'center',
    },
    font.heavy(font.size.mediumExtended),
);

const CategoriesList = styled('ul')({
    display: 'flex',
    flexWrap: 'wrap',
    padding: 0,
    marginBottom: 0,
    paddingBottom: pxToRem(8),
});

const FeedbackContainer = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: pxToRem(10),
    backgroundColor: color.dancingDolphinBlue,
    width: '100%',
    borderRadius: '4px',
    marginBottom: pxToRem(16),
    cursor: 'pointer',
});

const FeedbackText = styled.span({
    color: color.blueBayoux,
    ...font.medium(font.size.medium),
});

const ForwardArrowIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    height: '1rem',
    width: '1rem',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: 'center',
    border: 'none',
}));

export interface PreferencesModalProps {
    visible: boolean;
    onToggle: () => void;
    onApply: (choices: string[]) => void;
    choices: string[];
    setChoices: (choices: string[]) => void;
    onPressFeedback: () => void;
    isFeedbackSubmitted?: boolean;
}

const CloseIcon = styled.img({
    transform: 'scale(1)',
    cursor: 'pointer',
});

interface StyledIconProps {
    iconUrl: string;
}

export enum PreferencesModalTestID {
    FeedbackButton = 'PreferencesModalTestID.FeedbackButton',
}

export const PreferencesModal: React.FC<PreferencesModalProps> = ({
    onToggle,
    onApply,
    visible,
    choices,
    setChoices,
    onPressFeedback,
    isFeedbackSubmitted,
}) => {
    const { t } = useTranslation();
    const { loading, data } = useGetMemberFilterPreferencesQuery();
    const [localChoices, setLocalChoices] = useState<string[]>(choices);

    // We want to edit only the local choices a user makes in case they exit the modal without hitting apply
    const handleSelect = ({ name }) => {
        if (localChoices.includes(name)) {
            const next = localChoices.filter(choice => choice !== name);
            setLocalChoices(next);
        } else {
            const next = [...localChoices, name];
            setLocalChoices(next);
        }
    };

    const handleApplyFilters = () => {
        onToggle();
        onApply(localChoices);
        setChoices(localChoices);
    };

    return (
        <React.Fragment>
            <StyledModal
                visible={visible}
                onCancel={() => {
                    onToggle();
                    setChoices(choices);
                    setLocalChoices(choices);
                }}
                footer={null}
                width="auto"
                maskClosable={false}
                closeIcon={<CloseIcon src={closeIcon} />}
                title={<ModalHeader>{t('providers:preferences_modal.header')}</ModalHeader>}
            >
                {loading && <CenteredSpinner />}

                <CategoriesList>
                    {data?.memberFilterPreferences?.map((filter, i) => (
                        <CategoryItem
                            key={`${i}-preference-option`}
                            name={`${filter?.title} (${t(
                                'providers:preferences_modal.title_caption',
                            )}): `}
                            options={filter?.options}
                            handleSelect={handleSelect}
                            choices={localChoices}
                        />
                    ))}
                </CategoriesList>
                <FeedbackContainer
                    onClick={isFeedbackSubmitted ? voidFunction : onPressFeedback}
                    data-testid={PreferencesModalTestID.FeedbackButton}
                >
                    <FeedbackText>
                        {t(
                            isFeedbackSubmitted
                                ? 'providers:preferences_modal.left_feedback'
                                : 'providers:preferences_modal.feedback_text',
                        )}
                    </FeedbackText>
                    {!isFeedbackSubmitted && <ForwardArrowIcon iconUrl={chevronIcon} />}
                </FeedbackContainer>
                <Button variant="primary" onClick={handleApplyFilters} fluid>
                    {t('providers:preferences_modal.button')}
                </Button>
            </StyledModal>
        </React.Fragment>
    );
};
